<template>
                  <div class="tab-pane fade"
                    :class="{ 'active': 'Summary' === activeMenuSelection, 'show': 'Summary' === activeMenuSelection }"
                    id="Summary" role="tabpanel" aria-labelledby="Summary-tab">
                    <div class="wallet-summary-layout">
                      <!-- graph -->
                      <div class="wallet-summary-chart">
                        <div class="summary-balance-title">{{ t("balanceSummaryLabel") }}</div>
                        <PieChart
                          v-if="isSummaryActive"
                          :height="240"
                          :data="[parseFloat(totalEquitiesValue), parseFloat(freeFunds)]"
                          :labels="[t('positionsLabel'), t('freeFundsLabel')]"
                        ></PieChart>
                        <div class="summary-balance-total"><b>{{ formatPrice(totalBalance, currency, 2) }}</b></div>
                      </div>
                      <div class="wallet-summary-aggregate">
                        <div class="summary-row">
                          <div class="summary-label">
                            <span class="dot_money" />{{ t("freeFundsLabel") }}
                          </div>
                          <div class="summary-percent">{{ parseNumber(summary.moneyPercentage) }}%</div>
                          <div class="summary-value">{{ formatPrice(freeFunds, currency, 2).replace(' ', '&nbsp;') }}</div>
                        </div>
                        <div class="summary-row">
                          <div class="summary-label">
                            <span class="dot_instruments" />{{ t("positionsLabel") }}
                          </div>
                          <div class="summary-percent">{{ parseNumber(summary.equitiesPercentage) }}%</div>
                          <div class="summary-value">{{ formatPrice(totalEquitiesValue, currency, 2).replace(' ', '&nbsp;') }}</div>
                        </div>
                        <div class="summary-row summary-highlight">
                          <div class="summary-label">{{ t("excessFundsLabel") }}</div>
                          <div class="summary-value">{{ formatPrice(excessFunds, currency, 2).replace(' ', '&nbsp;') }}</div>
                        </div>
                        <div v-for="money in userMoneyRows" :key="money.c" class="summary-row summary-row-with-progress">
                          <div class="summary-label">
                            <strong>{{ money.c }}</strong>
                          </div>
                        <div class="summary-value">
                          <div v-if="isBaseCurrency(money.c)">{{ formatPrice(money.value, currency, 2).replace(' ', '&nbsp;') }}</div>
                          <div v-else>
                            <div class="summary-native-value">{{ formatPrice(money.a, money.c, 2).replace(' ', '&nbsp;') }}</div>
                            <div class="summary-base-value">{{ formatPrice(money.value, currency, 2).replace(' ', '&nbsp;') }}</div>
                          </div>
                        </div>
                        <div class="summary-progress-line">
                          <div class="summary-progress-percent">{{ parseNumber(money.percent) }}%</div>
                          <div class="summary-progress">
                            <div class="summary-progress-bar" :style="{ width: clampPercent(money.percent) + '%' }"></div>
                          </div>
                        </div>
                      </div>
                      <div v-for="group in summaryPositionGroupRows" :key="group.key" class="summary-row summary-row-with-progress">
                        <div class="summary-label">
                          <strong>{{ group.label }}</strong>
                        </div>
                        <div class="summary-value">{{ formatPrice(group.value, currency, 2).replace(' ', '&nbsp;') }}</div>
                        <div class="summary-progress-line">
                          <div class="summary-progress-percent">{{ parseNumber(group.percent) }}%</div>
                          <div class="summary-progress">
                            <div class="summary-progress-bar" :style="{ width: clampPercent(group.percent) + '%' }"></div>
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                  <div class="tab-pane fade"
                    :class="{ 'active': 'Cash' === activeMenuSelection, 'show': 'Cash' === activeMenuSelection }"
                    id="Cash" role="tabpanel" aria-labelledby="Cash-tab">
                    <WalletCashTotalsCard
                      :summary="summary"
                      :currency="currency"
                      :translator="translator"
                      :price-formatter="priceFormatter"
                    />
                    <div class="row">
                      <div v-for="money in userMoney" :key="money.c" class="col-md-4 col-sm-12">
                        <WalletMoneySummaryCard
                          :money="money"
                          :currency="currency"
                          :windows-width="windowsWidth"
                          :translator="translator"
                          :price-formatter="priceFormatter"
                          :number-parser="numberParser"
                          :money-ratio-resolver="moneyRatioResolver"
                          :total-money-percent-resolver="totalMoneyPercentResolver"
                          :total-balance-percent-resolver="totalBalancePercentResolver"
                          :settlement-resolver="settlementResolver"
                          variant="cash"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="tab-pane fade"
                    :class="{ 'active': 'Equities' === activeMenuSelection, 'show': 'Equities' === activeMenuSelection }"
                    id="Equities" role="tabpanel" aria-labelledby="Equities-tab">
                    <div class="positions-breakdown-switchbar">
                      <button
                        class="btn btn-outline-primary positions-breakdown-switch"
                        type="button"
                        @click.prevent="togglePositionsBreakdown"
                      >
                        {{ positionsBreakdownSwitchLabel }}
                      </button>
                    </div>
                    <div v-show="'Exchange' === activePositionsBreakdownTab" class="row">
                        <div
                          v-for="exchange in exchangeSummaries"
                          :key="exchange.id"
                          class="col-sm-12"
                          :class="{ 'col-md-4': 0 === parseInt(selectedExchangeCode), 'col-md-12': 0 !== parseInt(selectedExchangeCode) }">
                          <WalletExchangeSummaryCard
                            :exchange="exchange"
                            :visible="exchange.visible"
                            :currency="currency"
                            :windows-width="windowsWidth"
                            :all-equities-value-in-current-currency="allEquitiesValueInCurrentCurrency"
                            :total-balance="totalBalance"
                            :equities-shares-count="exchange.sharesCount"
                            :translator="translator"
                            :price-formatter="priceFormatter"
                            :number-parser="numberParser"
                            :open-breakdown="openBreakdown"
                          />
                        </div>
                      </div>
                      <div v-show="'InstrumentType' === activePositionsBreakdownTab" class="row">
                        <div
                          v-for="group in positionGroupRows"
                          :key="group.key"
                          class="col-sm-12 col-md-4"
                        >
                          <WalletInstrumentTypeSummaryCard
                            :group="group"
                            :currency="currency"
                            :windows-width="windowsWidth"
                            :total-balance="totalBalance"
                            :total-equities-value="totalEquitiesValue"
                            :translator="translator"
                            :price-formatter="priceFormatter"
                            :number-parser="numberParser"
                            :open-breakdown="openInstrumentTypeBreakdown"
                          />
                        </div>
                      </div>
                  </div>
</template>

<script>
import PieChart from "@/components/charts/PieChart.vue";
import WalletCashTotalsCard from "./WalletCashTotalsCard.vue";
import WalletExchangeSummaryCard from "./WalletExchangeSummaryCard.vue";
import WalletInstrumentTypeSummaryCard from "./WalletInstrumentTypeSummaryCard.vue";
import WalletMoneySummaryCard from "./WalletMoneySummaryCard.vue";

export default {
  name: "WalletSummary",
  components: {
    PieChart,
    WalletCashTotalsCard,
    WalletExchangeSummaryCard,
    WalletInstrumentTypeSummaryCard,
    WalletMoneySummaryCard
  },
  data() {
    return {
      activePositionsBreakdownTab: "Exchange"
    };
  },
  watch: {
    positionsBreakdownTab: {
      immediate: true,
      handler(value) {
        if ("InstrumentType" === value || "Exchange" === value) {
          this.activePositionsBreakdownTab = value;
        }
      }
    }
  },
  props: {
    activeMenuSelection: {
      type: String,
      required: true
    },
    summary: {
      type: Object,
      required: true
    },
    translator: {
      type: Function,
      required: true
    },
    priceFormatter: {
      type: Function,
      required: true
    },
    numberParser: {
      type: Function,
      required: true
    },
    moneyRatioResolver: {
      type: Function,
      required: true
    },
    totalMoneyPercentResolver: {
      type: Function,
      required: true
    },
    totalBalancePercentResolver: {
      type: Function,
      required: true
    },
    settlementResolver: {
      type: Function,
      required: true
    },
    openBreakdown: {
      type: Function,
      required: true
    },
    openInstrumentTypeBreakdown: {
      type: Function,
      required: true
    }
  },
  computed: {
    currency() {
      return this.summary.currency;
    },
    windowsWidth() {
      return this.summary.windowsWidth;
    },
    isSummaryActive() {
      return "Summary" === this.activeMenuSelection;
    },
    allEquitiesValueInCurrentCurrency() {
      return this.summary.allEquitiesValueInCurrentCurrency;
    },
    excessFunds() {
      return this.summary.excessFunds;
    },
    freeFunds() {
      return this.summary.freeFunds;
    },
    selectedExchangeCode() {
      return this.summary.selectedExchangeCode;
    },
    totalBalance() {
      return this.summary.totalBalance;
    },
    totalEquitiesValue() {
      return this.summary.totalEquitiesValue;
    },
    userMoney() {
      return this.summary.userMoney;
    },
    userMoneyRows() {
      return this.userMoney.map(money => {
        const value = this.getMoneyValueInSummaryCurrency(money);
        return {
          ...money,
          value,
          percent: 0 === this.totalBalance ? 0 : value / this.totalBalance * 100
        };
      });
    },
    positionGroupRows() {
      const labels = {
        stocksAndEtfs: this.t("stocksAndEtfsLabel"),
        bonds: this.t("bondsLabel"),
        reits: this.t("reitsLabel"),
        other: this.t("othersLabel")
      };

      return (this.summary.positionGroupSummaries || []).map(group => ({
        ...group,
        label: labels[group.key] || group.key
      }));
    },
    summaryPositionGroupRows() {
      const rows = this.positionGroupRows.map(group => ({ ...group }));
      const stocksAndEtfs = rows.find(group => "stocksAndEtfs" === group.key);
      const reits = rows.find(group => "reits" === group.key);

      if (stocksAndEtfs && reits) {
        stocksAndEtfs.value = (parseFloat(stocksAndEtfs.value) || 0) + (parseFloat(reits.value) || 0);
        stocksAndEtfs.avgValue = (parseFloat(stocksAndEtfs.avgValue) || 0) + (parseFloat(reits.avgValue) || 0);
        stocksAndEtfs.positionsCount = (parseInt(stocksAndEtfs.positionsCount) || 0) + (parseInt(reits.positionsCount) || 0);
        stocksAndEtfs.percent = 0 === this.totalBalance ? 0 : stocksAndEtfs.value / this.totalBalance * 100;
      }

      return rows.filter(group => "reits" !== group.key);
    },
    exchangeSummaries() {
      return this.summary.exchangeSummaries;
    },
    positionsBreakdownSwitchLabel() {
      return "Exchange" === this.activePositionsBreakdownTab
        ? this.t("showByInstrumentTypeLabel")
        : this.t("showByExchangeLabel");
    },
    positionsBreakdownTab() {
      return this.summary.positionsBreakdownTab;
    }
  },
  methods: {
    t(key, ...args) {
      return this.translator(key, ...args);
    },
    formatPrice(...args) {
      return this.priceFormatter(...args);
    },
    parseNumber(...args) {
      return this.numberParser(...args);
    },
    clampPercent(value) {
      const percent = parseFloat(value);
      if (!Number.isFinite(percent) || 0 > percent) {
        return 0;
      }
      if (100 < percent) {
        return 100;
      }
      return percent;
    },
    isBaseCurrency(currencyCode) {
      return currencyCode?.toString().toUpperCase() === this.currency?.toString().toUpperCase();
    },
    togglePositionsBreakdown() {
      this.activePositionsBreakdownTab = "Exchange" === this.activePositionsBreakdownTab
        ? "InstrumentType"
        : "Exchange";
    },
    getMoneyValueInSummaryCurrency(money) {
      if (money.c?.toString().toUpperCase() === this.currency?.toString().toUpperCase()) {
        return parseFloat(money.a) || 0;
      }

      const percent = this.totalBalancePercentResolver(money);
      return 0 === this.totalBalance ? 0 : this.totalBalance * percent / 100;
    }
  }
};
</script>

<style scoped>
.xsmall-font-size {
  font-size: x-small;
}

.greenColor {
  color: #74b21f;
}

.redColor {
  color: #ff0000;
}

.data-column-large {
  min-width: 4.5rem;
  vertical-align: middle;
  text-align: center;
  font-size: large;
}

.wallet-summary-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.wallet-summary-chart {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.summary-balance-title {
  font-size: 1.25rem;
  line-height: 1;
  margin: 0 0 -15px;
  text-align: center;
}

.summary-balance-total {
  font-size: 1.6rem;
  line-height: 1;
  margin: -10px 0 0;
  text-align: center;
  white-space: nowrap;
}

.wallet-summary-aggregate {
  width: 100%;
}

.positions-breakdown-switchbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.positions-breakdown-switch {
  min-width: 11rem;
}

.summary-row {
  align-items: center;
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) 4.5rem minmax(7rem, auto);
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
}

.summary-row + .summary-row {
  border-top: 1px solid #eef0f3;
}

.summary-highlight {
  background: #f0f0f0;
  font-weight: bold;
  grid-template-columns: minmax(8rem, 1fr) minmax(7rem, auto);
}

.summary-label {
  align-items: center;
  display: inline-flex;
  gap: 0.35rem;
}

.summary-percent,
.summary-value {
  text-align: right;
}

.summary-value {
  font-weight: bold;
}

.summary-native-value {
  font-size: 0.85em;
  font-weight: normal;
}

.summary-base-value {
  font-weight: bold;
}

.summary-row-with-progress {
  grid-template-areas:
    "label value value"
    "progress-line progress-line progress-line";
}

.summary-row-with-progress .summary-label {
  grid-area: label;
}

.summary-row-with-progress .summary-value {
  grid-area: value;
}

.summary-progress-line {
  align-items: center;
  display: grid;
  gap: 0.5rem;
  grid-area: progress-line;
  grid-template-columns: 4.5rem 1fr;
}

.summary-progress-percent {
  text-align: left;
}

.summary-progress {
  background: #edf0f2;
  border-radius: 999px;
  height: 0.45rem;
  overflow: hidden;
}

.summary-progress-bar {
  background: #27b35d;
  border-radius: inherit;
  height: 100%;
}

.dot_money,
.dot_instruments {
  border-radius: 50%;
  display: inline-block;
  height: 0.8rem;
  width: 0.8rem;
}

.dot_money {
  background: #4caf50;
}

.dot_instruments {
  background: #4285f4;
}

@media (max-width: 1199px) {
  .wallet-summary-chart {
    min-height: auto;
  }
}
</style>
